/**
 * Page-tour store (immersive help — ProjectTasks: "more interactive/immersive
 * learning in the help / what-this-page-does section").
 *
 * A page tour is a single-page, in-context spotlight sequence: it highlights
 * the key affordances on the CURRENT page one at a time with a short caption,
 * so "How this works" can SHOW the page instead of only describing it.
 *
 * Distinct from the #109 sandbox walkthrough (multi-page, scenario-scripted,
 * sandbox-only). This one is contextual, launched from the help modal on any
 * page, and never navigates away. It reuses the same spotlight visual idea but
 * stays deliberately lightweight (no persistence, no routing).
 */

import { create } from 'zustand'

interface PageTourState {
  /** helpKey of the tour currently running, or null. */
  activeTourKey: string | null
  stepIndex: number
  /**
   * 'tour' = the linear next/back spotlight stepper (PageTourOverlay).
   * 'explore' = the free-explore hover map: every section is a hotspot at once,
   * hover one to learn it (PageExploreOverlay). Same `helpKey -> steps` data.
   */
  mode: 'tour' | 'explore'
  start: (tourKey: string) => void
  startExplore: (tourKey: string) => void
  next: () => void
  prev: () => void
  exit: () => void
}

export const usePageTourStore = create<PageTourState>((set, get) => ({
  activeTourKey: null,
  stepIndex: 0,
  mode: 'tour',
  start: (tourKey) => set({ activeTourKey: tourKey, stepIndex: 0, mode: 'tour' }),
  startExplore: (tourKey) => set({ activeTourKey: tourKey, stepIndex: 0, mode: 'explore' }),
  next: () => set({ stepIndex: get().stepIndex + 1 }),
  prev: () => set({ stepIndex: Math.max(0, get().stepIndex - 1) }),
  exit: () => set({ activeTourKey: null, stepIndex: 0, mode: 'tour' }),
}))
